/** Shared themed discard prompt. Callers retain ownership of the underlying draft. */
export function createDiscardConfirmation() {
  let pending: { cancel(): void } | undefined;

  function ask(current: () => boolean): Promise<boolean> {
    if (pending || !current()) return Promise.resolve(false);
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = document.createElement("dialog");
    dialog.className = "discard-confirmation";
    dialog.dataset["testid"] = "discard-confirmation";
    const title = document.createElement("h2");
    title.id = `discard-confirmation-${crypto.randomUUID()}`;
    title.textContent = "Discard these changes?";
    dialog.setAttribute("aria-labelledby", title.id);
    const actions = document.createElement("div");
    actions.className = "discard-confirmation-actions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn-secondary";
    cancel.textContent = "Cancel";
    const discard = document.createElement("button");
    discard.type = "button";
    discard.className = "btn-danger";
    discard.textContent = "Discard";
    actions.append(cancel, discard);
    dialog.append(title, actions);
    return new Promise<boolean>((resolve) => {
      let settled = false;
      function finish(accepted: boolean, restore: boolean): void {
        if (settled) return;
        settled = true;
        const stillCurrent = current();
        // Removing the modal avoids native close() restoring an obsolete opener.
        // Only a cancelled, still-current interaction may explicitly restore it.
        dialog.remove();
        pending = undefined;
        if (restore && stillCurrent && previous?.isConnected &&
          previous.closest("[hidden], [inert]") === null) previous.focus();
        resolve(accepted && stillCurrent);
      }
      pending = { cancel: () => finish(false, false) };
      cancel.onclick = () => finish(false, true);
      discard.onclick = () => finish(true, false);
      dialog.addEventListener("cancel", (event) => {
        event.preventDefault();
        finish(false, true);
      });
      dialog.addEventListener("click", (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom) finish(false, true);
      });
      document.body.append(dialog);
      dialog.showModal();
      cancel.focus();
    });
  }

  return { ask, cancel: () => pending?.cancel() };
}
